import React, {
  useState,
} from 'react';
import clsx from 'clsx';
import Popover from '@mui/material/Popover';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import {
  PaymentMethodDTO,
} from 'dtos';
import {
  formatMoney,
} from '@populateui/utils';
import Editable from 'core/components/Editable';
import InsurancePaymentForm from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/InsurancePaymentForm';
import startCase from 'lodash/startCase';
import {
  formatDate,
} from 'utils/date';

type Props = {
  readonly?: boolean;
};

type InsurancePaymentIdleViewProps = PaymentMethodDTO & {
  paymentDate: string | null | undefined;
};

function InsurancePaymentIdleView({
  paymentDate,
  paymentAmount,
  discriminator,
  transactionIdentifier,
}: InsurancePaymentIdleViewProps) {
  const paymentMethodLabel = startCase((discriminator ?? 'Unknown').replace('Payment', ' Payment'));
  const dateLabel = formatDate(paymentDate ?? '');

  return (
    <div className="grid grid-cols-1 gap-1 w-full text-xs text-black">
      <div className="flex items-center gap-2">
        <strong>{formatMoney(paymentAmount ?? 0)}</strong>
        <span className="text-gray-600">{dateLabel}</span>
      </div>
      <div className="flex items-center gap-2 text-gray-600">
        <span>{paymentMethodLabel}</span>
        {transactionIdentifier ? <span className="text-gray-500">{transactionIdentifier}</span> : null}
      </div>
    </div>
  );
}

export default function InsurancePaymentPopover({ readonly = false }: Props) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const {
    reset,
    resetField,
    getValues,
    watch,
  } = useFormContext();
  const insurancePayment = watch('insurancePayment');
  const isEdit = !isNil(container);

  return (
    <div className={clsx('flex relative min-h-[32px]', { 'items-center': !isEdit })}>
      <Editable
        idleView={(
          <InsurancePaymentIdleView
            {...(insurancePayment?.paymentMethod ?? {})}
            paymentDate={insurancePayment?.paymentDate}
          />
        )}
        readonly={readonly}
        onShowControl={(e) => {
          setContainer(e.currentTarget);
          // to reset the state of the form,
          // so that resetField comes back to this point, not the initial default value
          reset({
            ...getValues(),
            insurancePayment,
          });
        }}
        onCancel={() => {
          resetField('insurancePayment');
        }}
        onSubmit={() => {
          reset({
            ...getValues(),
            insurancePayment,
          });
          return true;
        }}
      >
        {(onSubmit, onCancel) => (
          <Popover
            open={isEdit}
            onClose={() => {
              setContainer(null);
              onCancel();
            }}
            disableScrollLock
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            classes={{
              paper: '!w-[480px] max-w-[calc(100vw-1rem)] p-2 !overflow-y-visible !overflow-x-visible',
            }}
            anchorEl={container}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <InsurancePaymentForm
              readonly={readonly}
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </Popover>
        )}
      </Editable>
    </div>
  );
}
